import { dateToApiFormat } from '@/helpers/dateHelper';
import { currentPeriodStart, MONTHLY, YEARLY } from '@/pages/team/teamMember/annualLeave/leaveBalanceHelpers';
import {
  EmployeeLeaveAllowanceFragment,
  LeaveAllowancePeriod,
  useGetEmployeeLeaveAllowancesQuery
} from '@/services/gql/graphql.generated';
import { useGetLeaveType } from '@/services/leaveManagement/LeaveTypesProvider';
import { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import essStrings from '../../../ess.translations';

const useAllowances = (employeeId: string, period: LeaveAllowancePeriod) => {
  const [{ data }] = useGetEmployeeLeaveAllowancesQuery({
    variables: { employeeId, period, periodStart: dateToApiFormat(currentPeriodStart(period)) },
    requestPolicy: 'cache-and-network'
  });
  return data?.employeeLeaveAllowances ?? [];
};

const formatDays = (n: number) => `${n} ${n === 1 ? essStrings.common.day : essStrings.common.days}`;

const remainingClass = (remaining: number) =>
  remaining > 0 ? 'text-green-600' : remaining < 0 ? 'text-red-600' : 'text-black';

const BalanceSection: FC<{ title: string; allowances: EmployeeLeaveAllowanceFragment[] }> = ({ title, allowances }) => {
  if (allowances.length === 0) return null;

  const getLeaveType = useGetLeaveType();

  return (
    <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 gap-y-3 text-sm">
      <span className="text-xs font-semibold uppercase text-gray-500">{title}</span>
      <span className="text-right text-xs font-semibold uppercase text-gray-500">
        {essStrings.mySchedule.leave.taken}
      </span>
      <span className="text-right text-xs font-semibold uppercase text-gray-500">
        {essStrings.mySchedule.leave.remaining}
      </span>

      {allowances.map(a => (
        <div key={a.leaveType.id} className="contents">
          <span>
            {getLeaveType(a.leaveType.id)?.properties?.displayName || a.leaveType.name}
          </span>
          <span className="text-right">{formatDays(a.taken)}</span>
          <span className={twMerge('text-right', remainingClass(a.remaining))}>
            {formatDays(a.remaining)}
          </span>
        </div>
      ))}
    </div>
  );
};

export const MyScheduleLeaveBalanceSummary: FC<{ userId: string }> = ({ userId }) => {
  const yearly = useAllowances(userId, YEARLY);
  const monthly = useAllowances(userId, MONTHLY);

  if (yearly.length === 0 && monthly.length === 0) return null;

  return (
    <div className="mb-4 flex flex-col gap-4 border-b border-gray-200 pb-4">
      <BalanceSection title={essStrings.mySchedule.leave.annualLeaveType} allowances={yearly} />
      {/* <BalanceSection title={essStrings.mySchedule.leave.monthlyLeaveType} allowances={monthly} /> */}
    </div>
  );
};
